/**
 * FeaturedDemosSection.jsx — Section Home « Comprends en 30 secondes… »
 * Charte Code 2 Roues · Version MOTO · 9 démos (incluant contre-braquage)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Car, Gauge, Timer, Zap, Octagon, Coffee, Wine, Compass, AlertTriangle, Route, ArrowRight, Sparkles } from 'lucide-react';
import { FadeUp, Stagger, StaggerItem } from './MotionReveals';

const PREVIEW = [
  { slug: 'counter-steering', title: 'Contre-braquage', sub: '🏍️ Exclusif · pousse à gauche → tourne à gauche', icon: Compass, accent: 'from-red-600 to-rose-800', badge: 'NOUVEAU' },
  { slug: 'emergency-braking', title: 'Freinage urgence', sub: '🏍️ Dose pied + main · 70/30 ou chute !', icon: AlertTriangle, accent: 'from-red-700 to-orange-800', badge: 'NOUVEAU' },
  { slug: 'cornering', title: 'Trajectoire virage', sub: '🏍️ Extérieur · intérieur · extérieur', icon: Route, accent: 'from-orange-600 to-red-700', badge: 'NOUVEAU' },
  { slug: 'vehicle-blindspot', title: 'Angles morts', sub: 'Où le motard disparaît pour une voiture', icon: Car, accent: 'from-amber-600 to-red-700' },
  { slug: 'reaction-time', title: 'Temps de réaction', sub: 'Classement hebdo · défie tes amis', icon: Timer, accent: 'from-emerald-600 to-emerald-800' },
  { slug: 'kinetic', title: 'Énergie cinétique', sub: 'Choc à 50 km/h = chute du 4ᵉ étage', icon: Zap, accent: 'from-rose-600 to-rose-900' },
  { slug: 'visual-tunnel', title: 'Effet tunnel', sub: 'À 130 km/h, champ visuel = 30°', icon: Gauge, accent: 'from-blue-600 to-blue-900' },
  { slug: 'fatigue', title: 'Test PVT NASA', sub: 'Classement hebdo · 30 s · méthode NASA', icon: Coffee, accent: 'from-amber-700 to-amber-900' },
  { slug: 'alcohol-vision', title: 'Vision sous alcool', sub: 'Slider 0→1,2 g/L · scène floutée', icon: Wine, accent: 'from-purple-600 to-purple-900' },
  { slug: 'mariotte', title: 'Tache aveugle', sub: 'Peut masquer une moto à 20 m', icon: Eye, accent: 'from-red-700 to-red-900' },
  { slug: 'signs-discrimination', title: 'Panneaux flash', sub: 'Panneau flashé 600 ms · 5 manches', icon: Octagon, accent: 'from-yellow-600 to-orange-800' },
];

export default function FeaturedDemosSection({ variant = 'home' }) {
  const isCompact = variant === 'dashboard';
  return (
    <section
      className={`max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 ${isCompact ? 'mt-8 sm:mt-10' : 'mt-10 sm:mt-16 lg:mt-24'}`}
      data-testid="featured-demos-section"
    >
      <FadeUp>
        <div className="relative rounded-2xl sm:rounded-3xl border border-red-700/40 bg-gradient-to-br from-red-950/40 via-[#150a0a] to-[#0d0d0e] p-4 sm:p-7 lg:p-10 overflow-hidden">
          {/* Décorations animées */}
          <div className="absolute -top-32 -right-32 w-64 sm:w-96 h-64 sm:h-96 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
          <motion.div
            className="absolute -bottom-24 left-1/3 w-48 sm:w-72 h-48 sm:h-72 rounded-full bg-red-500/10 blur-3xl pointer-events-none"
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="relative">
            <div className="flex flex-wrap items-center gap-2 mb-2.5 sm:mb-4">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600/15 border border-red-700/40 text-[9px] sm:text-[10.5px] uppercase tracking-[0.22em] text-red-300 font-black">
                <Sparkles className="w-3 h-3" /> 9 démos pédagogiques · uniques en France
              </span>
            </div>

            <h2 className={`font-serif-editorial text-white tracking-tight leading-[1.02] ${
              isCompact
                ? 'text-[22px] sm:text-[30px] lg:text-[38px]'
                : 'text-[24px] sm:text-[40px] lg:text-[56px]'
            }`}>
              {isCompact
                ? <>Teste tes <span className="text-red-500">réflexes</span>, ta <span className="text-red-500">vision</span>, ton <span className="text-red-500">pilotage</span>.</>
                : <>Comprends en 30 secondes<br /><span className="text-red-500">ce qu'un cours met 30 minutes.</span></>
              }
            </h2>

            <p className="mt-3 sm:mt-4 text-white/70 text-[13px] sm:text-[15px] max-w-3xl leading-relaxed">
              {isCompact
                ? 'Démos interactives calibrées pour la moto : contre-braquage, angles morts, PVT NASA, temps de réaction, vision sous alcool…'
                : '9 démonstrations interactives calibrées par des moniteurs ECSR, adaptées MOTO : contre-braquage (exclusif moto !), angles morts vue du dessus, effet tunnel selon la vitesse, test PVT NASA, vision sous alcool… Interactives · gratuites · sans inscription.'
              }
            </p>

            {/* Demo grid preview — RESPONSIVE 2 → 3 → 5 colonnes */}
            <Stagger
              className="grid gap-2 sm:gap-2.5 lg:gap-3 mt-4 sm:mt-6 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
              staggerChildren={0.04}
            >
              {PREVIEW.slice(0, isCompact ? 5 : 9).map((d) => {
                const Icon = d.icon;
                return (
                  <StaggerItem key={d.slug}>
                    <Link
                      to={`/demos-interactives#${d.slug}`}
                      data-testid={`featured-demo-${d.slug}`}
                      className={`group block relative rounded-xl overflow-hidden border border-white/10 bg-gradient-to-br ${d.accent} p-2.5 sm:p-3.5 h-full hover:scale-[1.03] active:scale-95 transition-transform shadow-lg shadow-black/30 min-h-[90px] sm:min-h-[110px]`}
                    >
                      {d.badge && (
                        <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 border border-white/30 text-white text-[8px] font-black uppercase tracking-widest">
                          {d.badge}
                        </span>
                      )}
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center text-white mb-1.5 sm:mb-2">
                        <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </div>
                      <div className="font-serif-editorial text-white text-[12.5px] sm:text-[15px] lg:text-[17px] leading-tight">{d.title}</div>
                      <div className="text-white/80 text-[9.5px] sm:text-[11px] mt-1 line-clamp-2 leading-tight">{d.sub}</div>
                    </Link>
                  </StaggerItem>
                );
              })}
            </Stagger>

            <div className="mt-5 sm:mt-7 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-4">
              <Link
                to="/demos-interactives"
                data-testid="featured-demos-cta"
                className="inline-flex items-center justify-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] sm:text-[14px] font-black uppercase tracking-widest shadow-xl shadow-red-900/40 hover:scale-[1.03] active:scale-95 transition"
              >
                Découvrir les 9 démos <ArrowRight size={15} />
              </Link>
              <div className="text-center sm:text-left text-white/55 text-[11px] sm:text-[12.5px] font-mono-spec">
                Gratuit · sans inscription · <span className="hidden sm:inline">projetable en salle</span><span className="sm:hidden">projetable</span>
              </div>
            </div>
          </div>
        </div>
      </FadeUp>
    </section>
  );
}
